// src/components/HeadingTooltipTestFixture.jsx
//
// DEV-ONLY test fixture for HeadingTooltip viewport-overflow tests.
// Accessible at /?__e2e=tooltip-flip in development mode only.
// Never imported in production — guarded by import.meta.env.DEV in App.jsx.
//
// Bottom-edge trigger (task-544): a fixed-positioned trigger element sits at
// the very bottom of the viewport (bottom: 0). When hovered, HeadingTooltip
// opens. Because the trigger is flush with the bottom edge, the default
// "4px below the trigger" position would overflow the viewport, so the
// clamping logic in HeadingTooltip must flip the popup to appear ABOVE the
// trigger instead.
//
// Right-edge trigger (task-651): a fixed-positioned trigger element sits at
// the very right edge of the viewport (right: 0). When hovered, HeadingTooltip
// opens. Because the trigger is flush with the right edge, the default
// "left-aligned to trigger" position would cause the popup to overflow the
// right side of the viewport, so the clamping logic in HeadingTooltip must
// shift the popup leftward so it stays fully on-screen.
//
// Playwright uses this fixture to assert viewport-clamping behaviour in real
// Chromium (jsdom reports zero dimensions for all elements).

import React, { useRef } from "react";
import { useHoverIntent } from "./useHoverIntent.js";
import { T } from "../app/tokens.js";
import { HeadingTooltip } from "./HeadingTooltip.jsx";

const POPUP_STYLE = {
  background:    T.white,
  color:         T.navy,
  border:        `0.5px solid ${T.neutralBorder}`,
  borderRadius:  8,
  boxShadow:     T.shadowCardSm,
  padding:       "12px 16px",
  zIndex:        20,
  pointerEvents: "none",
  fontSize: T.fsBase,
  lineHeight:    "1.5",
  minWidth:      180,
};

export function HeadingTooltipTestFixture() {
  const { open, triggerProps } = useHoverIntent();
  const triggerRef = useRef(null);
  const { open: rightOpen, triggerProps: rightTriggerProps } = useHoverIntent();
  const rightTriggerRef = useRef(null);

  return (
    <div
      data-testid="heading-tooltip-flip-fixture"
      style={{ fontFamily: "sans-serif" }}
    >
      {/* Instruction text at the top of the page */}
      <p style={{ padding: 16, margin: 0, color: T.textMuted, fontSize: T.fsBase }}>
        HeadingTooltip overflow fixture (task-544, task-651).
        Hover the triggers to test bottom-edge flip and right-edge clamp.
      </p>

      {/* Trigger element fixed at the very bottom of the viewport.
          HeadingTooltip measures the trigger's bounding rect via triggerRef,
          so it sees rect.bottom ≈ window.innerHeight and must flip upward. */}
      <div
        style={{
          position: "fixed",
          bottom:   0,
          left:     0,
          right:    0,
          display:  "flex",
          alignItems: "center",
          background: T.defaultDotBg,
          borderTop:  `1px solid ${T.grayBorderXLight}`,
          padding:    "0 24px",
          height:     40,
          zIndex:     10,
        }}
      >
        <table style={{ borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th
                ref={triggerRef}
                data-testid="tooltip-flip-trigger"
                {...triggerProps}
                tabIndex={0}
                style={{
                  padding:       "8px 12px",
                  fontSize: T.fsSm,
                  fontWeight: T.fwBold,
                  letterSpacing: "0.07em",
                  textTransform: "uppercase",
                  color:         T.textDim,
                  cursor:        "default",
                  userSelect:    "none",
                  whiteSpace:    "nowrap",
                }}
              >
                Urgency ▲ hover me
              </th>
            </tr>
          </thead>
        </table>

        {open && (
          <HeadingTooltip
            triggerRef={triggerRef}
            data-testid="tooltip-flip-popup"
            role="tooltip"
            id="tooltip-flip-popup"
            style={POPUP_STYLE}
          >
            <div>
              <div style={{ marginBottom: 6, fontWeight: T.fwSemibold }}>Color guide</div>
              <div>🟢 Active (24 mo+)</div>
              <div>🟡 Upcoming (12-24 mo)</div>
              <div>🔴 Urgent (&lt;12 mo)</div>
              <div>⚫ Expired</div>
            </div>
          </HeadingTooltip>
        )}
      </div>

      {/* Trigger element fixed at the very right edge of the viewport.
          HeadingTooltip measures the trigger's bounding rect via rightTriggerRef,
          so rect.left ≈ window.innerWidth and the default left-aligned popup would
          overflow the right side of the viewport. The clamping logic must shift
          the popup leftward so its right edge stays within window.innerWidth. */}
      <div
        style={{
          position:   "fixed",
          top:        "50%",
          right:      0,
          transform:  "translateY(-50%)",
          display:    "flex",
          alignItems: "center",
          background: T.defaultDotBg,
          borderLeft: `1px solid ${T.grayBorderXLight}`,
          padding:    "0 8px",
          height:     40,
          zIndex:     10,
        }}
      >
        <table style={{ borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th
                ref={rightTriggerRef}
                data-testid="tooltip-right-edge-trigger"
                {...rightTriggerProps}
                tabIndex={0}
                style={{
                  padding:       "8px 12px",
                  fontSize: T.fsSm,
                  fontWeight: T.fwBold,
                  letterSpacing: "0.07em",
                  textTransform: "uppercase",
                  color:         T.textDim,
                  cursor:        "default",
                  userSelect:    "none",
                  whiteSpace:    "nowrap",
                }}
              >
                Status ▲ hover me
              </th>
            </tr>
          </thead>
        </table>

        {rightOpen && (
          <HeadingTooltip
            triggerRef={rightTriggerRef}
            data-testid="tooltip-right-edge-popup"
            role="tooltip"
            id="tooltip-right-edge-popup"
            style={POPUP_STYLE}
          >
            <div>
              <div style={{ marginBottom: 6, fontWeight: T.fwSemibold }}>Color guide</div>
              <div>🟢 Active (24 mo+)</div>
              <div>🟡 Upcoming (12-24 mo)</div>
              <div>🔴 Urgent (&lt;12 mo)</div>
              <div>⚫ Expired</div>
            </div>
          </HeadingTooltip>
        )}
      </div>
    </div>
  );
}
